import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  ResponsiveRender,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import { video } from './index.css'
import titleLogo from './logo.jpg'
import qrCode from './qr-code.png'

type Props = {
  metadata?: {
    __typename: string
    page: number
    page_size: number
    total_count: number
  }
  footerOnScreen: boolean
  listingsOnScreen: boolean
}
const InterRentProfile = ({
  metadata,
  footerOnScreen,
  listingsOnScreen
}: Props) => {
  const { t } = useTranslation('interrent')
  const profile = {
    title: t('interrent:InterRent'),
    url: '/company/interrent'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <video autoPlay loop muted playsInline className={video}>
            <source
              src="/videos/interrent-banner-mobile.mp4"
              type="video/mp4"
              media="(max-width: 599px)"
            />
            <source
              src="/videos/interrent-banner.mp4"
              type="video/mp4"
              media="(min-width: 600px)"
            />
          </video>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={60}
                height={39}
                alt="InterRent"
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('interrent:InterRent | love it here™')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t(`interrent:subtitle`)}
                  </Text>
                  <Text size="standard">{t('interrent:description')}</Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`tel: 833-284-7368`}
                    rel="noreferrer noopener"
                  >
                    833-AT-IRENT (833-284-7368)
                  </TextLink>
                </Box>

                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.irent.com/en"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.irent.com
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={markerIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`https://www.irent.com/en/contact-us`}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    Contact us
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('interrent:Our Portfolio')}</Heading>
      </Box>
      <ResponsiveRender above="tablet">
        <Box
          position={footerOnScreen && listingsOnScreen ? 'absolute' : 'fixed'}
          zIndex="dropdown"
          border="borderNeutralLight"
          background="bodyWhite"
          display="flex"
          style={{
            bottom: '20px',
            right: '10px'
          }}
          paddingBottom="xsmall"
        >
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            style={{ width: '158px', height: '175px' }}
          >
            <Image
              src={qrCode}
              alt="InterRent Living Qr"
              width={160}
              height={160}
              placeholder="blur"
            />
            <Text align="center" size="xsmall" weight="medium">
              {t('interrent:QR description')}
            </Text>
          </Box>
        </Box>
      </ResponsiveRender>
    </>
  )
}

export default InterRentProfile
